// Fills and signs the YEN distribution form (public/docs/YEN_DISTRIBUTION_FORM.pdf).
//
// The template has no fillable PDF fields, so every value is stamped at a
// fixed position. Text is rendered on a <canvas> (direction: rtl) and placed
// as an image, because pdf-lib has no RTL/bidi support. The browser handles
// Hebrew, mixed Hebrew/English and numbers correctly this way.
//
// Positions below are in PDF points measured from the TOP of an A4 page
// (595.32 x 841.92). "line" = the y of the blank's underline. They're tied
// to the Jul-26 version of the form: if the PDF layout changes, re-measure.

import { PDFDocument } from 'pdf-lib';

export const TEMPLATE_URL = '/docs/YEN_DISTRIBUTION_FORM.pdf';
export const MAX_TRACKS = 14;

const PAGE_H = 841.92;
const TEXT_FONT = "'Frank Ruhl Libre', 'David', 'Times New Roman', serif";

const LAYOUT = {
  p1: {
    name: { x0: 381, x1: 472, line: 168, size: 11 },
    idNum: { x0: 273, x1: 352, line: 168, size: 11, ltr: true },
    date: { x0: 382, x1: 461, line: 754.8, size: 11, ltr: true },
    sig: { x0: 129, x1: 272, line: 754.8, h: 38 },
  },
  p2: {
    deedDate: { x0: 123, x1: 177, line: 123, size: 10, ltr: true },
    rowLines: [186.1, 198.6, 211.2, 223.7, 236.2, 248.7, 261.1, 273.7, 286.3, 298.7, 311.2, 323.7, 336.2, 348.7, 361.3],
    cols: {
      performer: { x0: 392.2, x1: 502.4 },
      track: { x0: 246.9, x1: 391.8 },
      release: { x0: 115.3, x1: 246.4 },
    },
    notes: { x0: 131, x1: 486, line: 408.1, size: 9 },
    date: { x0: 436, x1: 486, line: 479.5, size: 10, ltr: true },
    sig: { x0: 152, x1: 279, line: 479.5, h: 36 },
  },
  p3: {
    deedDate: { x0: 422, x1: 488, line: 188.5, size: 10, ltr: true },
    artistSig: { x0: 56, x1: 196, line: 660, h: 40 },
    artistDate: { x0: 204, x1: 278, line: 660, size: 10, ltr: true },
    labelSig: { x0: 317, x1: 456, line: 660, h: 40 },
    labelDate: { x0: 464, x1: 539, line: 660, size: 10, ltr: true },
  },
};

let fontReady = null;
function ensureFont() {
  if (fontReady) return fontReady;
  fontReady = (async () => {
    try {
      if (!document.getElementById('frank-ruhl-font')) {
        const link = document.createElement('link');
        link.id = 'frank-ruhl-font';
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@500&display=swap';
        document.head.appendChild(link);
      }
      await document.fonts.load("500 40px 'Frank Ruhl Libre'", 'אבג');
    } catch (_) { /* falls back to system serif */ }
  })();
  return fontReady;
}

export function formatDateIL(d = new Date()) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/* Israeli ID check-digit validation (9 digits, Luhn-style). */
export function isValidIsraeliId(raw) {
  const id = String(raw || '').trim();
  if (!/^\d{5,9}$/.test(id)) return false;
  const padded = id.padStart(9, '0');
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let n = Number(padded[i]) * ((i % 2) + 1);
    if (n > 9) n -= 9;
    sum += n;
  }
  return sum % 10 === 0;
}

function dataUrlToBytes(dataUrl) {
  const b64 = dataUrl.split(',')[1];
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToBase64(bytes) {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

/* Renders one line of text to a PNG sized exactly to the target box. */
function renderTextPng(text, { widthPt, size, ltr = false, align, color = '#111', weight = 500 }) {
  const scale = 4;
  const heightPt = size * 1.55;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(widthPt * scale);
  canvas.height = Math.ceil(heightPt * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.direction = ltr ? 'ltr' : 'rtl';
  ctx.fillStyle = color;
  ctx.textBaseline = 'alphabetic';

  // Shrink to fit if the value is longer than the blank.
  let fs = size;
  const pad = 2;
  ctx.font = `${weight} ${fs}px ${TEXT_FONT}`;
  while (fs > 5 && ctx.measureText(text).width > widthPt - pad * 2) {
    fs -= 0.5;
    ctx.font = `${weight} ${fs}px ${TEXT_FONT}`;
  }

  const a = align || 'center';
  ctx.textAlign = a === 'center' ? 'center' : a === 'right' ? 'right' : 'left';
  const x = a === 'center' ? widthPt / 2 : a === 'right' ? widthPt - pad : pad;
  ctx.fillText(text, x, heightPt - size * 0.38);
  return { bytes: dataUrlToBytes(canvas.toDataURL('image/png')), heightPt };
}

async function stampText(pdfDoc, page, text, box, opts = {}) {
  if (!text) return;
  const widthPt = box.x1 - box.x0;
  const { bytes, heightPt } = renderTextPng(String(text), { widthPt, size: box.size, ltr: box.ltr, ...opts });
  const img = await pdfDoc.embedPng(bytes);
  // Bottom of the image sits just above the underline.
  page.drawImage(img, { x: box.x0, y: PAGE_H - box.line + 0.8, width: widthPt, height: heightPt });
}

async function stampSignature(pdfDoc, page, sigPngBytes, box) {
  const img = await pdfDoc.embedPng(sigPngBytes);
  const maxW = box.x1 - box.x0;
  const ratio = img.width / img.height;
  let h = box.h;
  let w = h * ratio;
  if (w > maxW) { w = maxW; h = w / ratio; }
  const x = box.x0 + (maxW - w) / 2;
  page.drawImage(img, { x, y: PAGE_H - box.line + 0.5, width: w, height: h });
}

async function stampFooter(pdfDoc, pages, text) {
  for (const page of pages) {
    const { bytes, heightPt } = renderTextPng(text, { widthPt: 400, size: 6.5, color: '#8a8a8a', weight: 400 });
    const img = await pdfDoc.embedPng(bytes);
    page.drawImage(img, { x: (595.32 - 400) / 2, y: 18, width: 400, height: heightPt });
  }
}

/**
 * Builds the artist-signed PDF.
 * data: { fullName, idNumber, tracks: [{performer, track, release}], notes, signaturePng (Uint8Array), signedAt (Date) }
 * Returns Uint8Array.
 */
export async function buildSignedForm(data) {
  await ensureFont();
  const signedAt = data.signedAt || new Date();
  const date = formatDateIL(signedAt);

  const templateBytes = await fetch(TEMPLATE_URL).then(r => {
    if (!r.ok) throw new Error('לא ניתן לטעון את הטופס');
    return r.arrayBuffer();
  });
  const pdfDoc = await PDFDocument.load(templateBytes);
  const [p1, p2, p3] = pdfDoc.getPages();
  const L = LAYOUT;

  // Page 1: declaration
  await stampText(pdfDoc, p1, data.fullName, L.p1.name);
  await stampText(pdfDoc, p1, data.idNumber, L.p1.idNum);
  await stampText(pdfDoc, p1, date, L.p1.date);
  await stampSignature(pdfDoc, p1, data.signaturePng, L.p1.sig);

  // Page 2: appendix A, track list
  await stampText(pdfDoc, p2, date, L.p2.deedDate);
  const rows = (data.tracks || []).slice(0, MAX_TRACKS);
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const line = L.p2.rowLines[i + 1];
    for (const key of ['performer', 'track', 'release']) {
      const col = L.p2.cols[key];
      await stampText(pdfDoc, p2, r[key], { ...col, line: line - 0.6, size: 8.5 }, { align: 'right' });
    }
  }
  if (data.notes) await stampText(pdfDoc, p2, data.notes, L.p2.notes, { align: 'right' });
  await stampText(pdfDoc, p2, date, L.p2.date);
  await stampSignature(pdfDoc, p2, data.signaturePng, L.p2.sig);

  // Page 3: appendix B, artist side
  await stampText(pdfDoc, p3, date, L.p3.deedDate);
  await stampSignature(pdfDoc, p3, data.signaturePng, L.p3.artistSig);
  await stampText(pdfDoc, p3, date, L.p3.artistDate);

  const stamp = `${date} ${String(signedAt.getHours()).padStart(2, '0')}:${String(signedAt.getMinutes()).padStart(2, '0')}`;
  await stampFooter(pdfDoc, [p1, p2, p3], `נחתם דיגיטלית ע״י ${data.fullName} באמצעות yensound.com · ${stamp}`);

  pdfDoc.setTitle(`הצהרה בדבר העברת זכויות – ${data.fullName}`);
  pdfDoc.setAuthor(data.fullName);
  pdfDoc.setCreator('yensound.com');
  return pdfDoc.save();
}

/**
 * Adds the label's countersignature (page 3, "חתימה בשם YEN SOUND" + date)
 * to an already artist-signed PDF. Returns Uint8Array.
 */
export async function countersignForm(signedPdfBytes, signaturePng, signedAt = new Date()) {
  await ensureFont();
  const pdfDoc = await PDFDocument.load(signedPdfBytes);
  const p3 = pdfDoc.getPages()[2];
  await stampSignature(pdfDoc, p3, signaturePng, LAYOUT.p3.labelSig);
  await stampText(pdfDoc, p3, formatDateIL(signedAt), LAYOUT.p3.labelDate);
  return pdfDoc.save();
}

/**
 * PIL copy: pages 1–2 only. Page 3 (נספח ב׳) is internal between the label
 * and the artist and must never go out to PIL. Returns Uint8Array.
 */
export async function pilCopy(fullPdfBytes) {
  const src = await PDFDocument.load(fullPdfBytes);
  const out = await PDFDocument.create();
  const pages = await out.copyPages(src, [0, 1]);
  pages.forEach(p => out.addPage(p));
  out.setTitle(`${src.getTitle() || 'הצהרה בדבר העברת זכויות'} (עמ׳ 1–2)`);
  out.setCreator('yensound.com');
  return out.save();
}

export function downloadPdf(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
